import {
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import {
  useCallback
} from 'react';

import type {
  AiPlausibilityCheckResponse,
  AssignCompanySignerParams,
  AssignDokaResponsibleParams,
  ChangeResponsibleTeamParams,
  CreateOnboardingRequestExistingModel,
  CreateOnboardingRequestNewModel,
  ExportRequestsParams,
  GetOnboardingRequestByIdResponse,
  GetOnboardingRequestDetailResponse,
  GetOnboardingRequestsResponse,
  GetRequestsParams,
  ProblemDetails,
  UpdateOnboardingRequestModel,
  UpdateStateManuallyParams,
  VerifyOnboardingRequestModel
} from '../../models';

import { useCustomWelcomeClient } from '../../custom-client.tsx';
import type { ErrorType } from '../../custom-client.tsx';

type AwaitedInput<T> = PromiseLike<T> | T;

      type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;




const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * Returns all requests<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice<br>welcome.doka.com/onboarding-dokathreesixty
 */
export const useGetRequestsHook = () => {
        const getRequests = useCustomWelcomeClient<GetOnboardingRequestsResponse>();

        return useCallback((
    params?: GetRequestsParams,
 signal?: AbortSignal
) => {


        return getRequests(
          {url: `/requests`, method: 'GET',
        params, signal
    },
          );
        }, [getRequests])
      }




export const getGetRequestsQueryKey = (params?: GetRequestsParams,) => {
    return [
    `/requests`, ...(params ? [params] : [])
    ] as const;
    }


export const useGetRequestsQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetRequestsHook>>>, TError = ErrorType<ProblemDetails>>(params?: GetRequestsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRequestsHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetRequestsQueryKey(params);

  const getRequests =  useGetRequestsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetRequestsHook>>>> = ({ signal }) => getRequests(params, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRequestsHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetRequestsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetRequestsHook>>>>
export type GetRequestsQueryError = ErrorType<ProblemDetails>


export function useGetRequests<TData = Awaited<ReturnType<ReturnType<typeof useGetRequestsHook>>>, TError = ErrorType<ProblemDetails>>(
 params: undefined |  GetRequestsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRequestsHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetRequestsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetRequestsHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetRequests<TData = Awaited<ReturnType<ReturnType<typeof useGetRequestsHook>>>, TError = ErrorType<ProblemDetails>>(
 params?: GetRequestsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRequestsHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetRequestsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetRequestsHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetRequests<TData = Awaited<ReturnType<ReturnType<typeof useGetRequestsHook>>>, TError = ErrorType<ProblemDetails>>(
 params?: GetRequestsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRequestsHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetRequests<TData = Awaited<ReturnType<ReturnType<typeof useGetRequestsHook>>>, TError = ErrorType<ProblemDetails>>(
 params?: GetRequestsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRequestsHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetRequestsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Returns a request by its id<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useGetRequestByIdHook = () => {
        const getRequestById = useCustomWelcomeClient<GetOnboardingRequestByIdResponse>();

        return useCallback((
    id: string,
 signal?: AbortSignal
) => {


        return getRequestById(
          {url: `/requests/${id}`, method: 'GET', signal
    },
          );
        }, [getRequestById])
      }




export const getGetRequestByIdQueryKey = (id: string,) => {
    return [
    `/requests/${id}`
    ] as const;
    }


export const useGetRequestByIdQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetRequestByIdHook>>>, TError = ErrorType<ProblemDetails>>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRequestByIdHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetRequestByIdQueryKey(id);

  const getRequestById =  useGetRequestByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetRequestByIdHook>>>> = ({ signal }) => getRequestById(id, signal);





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRequestByIdHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetRequestByIdQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetRequestByIdHook>>>>
export type GetRequestByIdQueryError = ErrorType<ProblemDetails>


export function useGetRequestById<TData = Awaited<ReturnType<ReturnType<typeof useGetRequestByIdHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRequestByIdHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetRequestByIdHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetRequestByIdHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetRequestById<TData = Awaited<ReturnType<ReturnType<typeof useGetRequestByIdHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRequestByIdHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetRequestByIdHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetRequestByIdHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetRequestById<TData = Awaited<ReturnType<ReturnType<typeof useGetRequestByIdHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRequestByIdHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetRequestById<TData = Awaited<ReturnType<ReturnType<typeof useGetRequestByIdHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRequestByIdHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetRequestByIdQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Runs AI plausibility checks for an onboarding request and returns the structured result.<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding-backoffice
 */
export const useAiPlausibilityCheckHook = () => {
        const aiPlausibilityCheck = useCustomWelcomeClient<AiPlausibilityCheckResponse>();

        return useCallback((
    id: string,
 signal?: AbortSignal
) => {


        return aiPlausibilityCheck(
          {url: `/requests/${id}/ai-plausibility`, method: 'POST', signal
    },
          );
        }, [aiPlausibilityCheck])
      }




export const useAiPlausibilityCheckMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useAiPlausibilityCheckHook>>>, TError,AiPlausibilityCheckMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useAiPlausibilityCheckHook>>>, TError,AiPlausibilityCheckMutationVariables, TContext> => {

const mutationKey = ['aiPlausibilityCheck'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const aiPlausibilityCheck =  useAiPlausibilityCheckHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useAiPlausibilityCheckHook>>>, AiPlausibilityCheckMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  aiPlausibilityCheck(id,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AiPlausibilityCheckMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useAiPlausibilityCheckHook>>>>

    export type AiPlausibilityCheckMutationError = ErrorType<ProblemDetails>
    export type AiPlausibilityCheckMutationVariables = {id: string}

    export const useAiPlausibilityCheck = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useAiPlausibilityCheckHook>>>, TError,AiPlausibilityCheckMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useAiPlausibilityCheckHook>>>,
        TError,
        AiPlausibilityCheckMutationVariables,
        TContext
      > => {
      return useMutation(useAiPlausibilityCheckMutationOptions(options), queryClient);
    }
    /**
 * Assigns a Company signer to a request<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useAssignCompanySignerHook = () => {
        const assignCompanySigner = useCustomWelcomeClient<void>();

        return useCallback((
    id: string,
    params?: AssignCompanySignerParams,
 signal?: AbortSignal
) => {


        return assignCompanySigner(
          {url: `/requests/${id}/assign-company-signer`, method: 'PUT',
        params, signal
    },
          );
        }, [assignCompanySigner])
      }




export const useAssignCompanySignerMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useAssignCompanySignerHook>>>, TError,AssignCompanySignerMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useAssignCompanySignerHook>>>, TError,AssignCompanySignerMutationVariables, TContext> => {

const mutationKey = ['assignCompanySigner'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const assignCompanySigner =  useAssignCompanySignerHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useAssignCompanySignerHook>>>, AssignCompanySignerMutationVariables> = (props) => {
          const {id,params} = props ?? {};

          return  assignCompanySigner(id,params,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AssignCompanySignerMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useAssignCompanySignerHook>>>>

    export type AssignCompanySignerMutationError = ErrorType<ProblemDetails>
    export type AssignCompanySignerMutationVariables = {id: string;params?: AssignCompanySignerParams}

    export const useAssignCompanySigner = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useAssignCompanySignerHook>>>, TError,AssignCompanySignerMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useAssignCompanySignerHook>>>,
        TError,
        AssignCompanySignerMutationVariables,
        TContext
      > => {
      return useMutation(useAssignCompanySignerMutationOptions(options), queryClient);
    }
    /**
 * Assigns a Doka responsible to a request<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding-backoffice
 */
export const useAssignDokaResponsibleHook = () => {
        const assignDokaResponsible = useCustomWelcomeClient<void>();

        return useCallback((
    id: string,
    params: AssignDokaResponsibleParams,
 signal?: AbortSignal
) => {


        return assignDokaResponsible(
          {url: `/requests/${id}/assign-doka-responsible`, method: 'PUT',
        params, signal
    },
          );
        }, [assignDokaResponsible])
      }




export const useAssignDokaResponsibleMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useAssignDokaResponsibleHook>>>, TError,AssignDokaResponsibleMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useAssignDokaResponsibleHook>>>, TError,AssignDokaResponsibleMutationVariables, TContext> => {

const mutationKey = ['assignDokaResponsible'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const assignDokaResponsible =  useAssignDokaResponsibleHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useAssignDokaResponsibleHook>>>, AssignDokaResponsibleMutationVariables> = (props) => {
          const {id,params} = props ?? {};

          return  assignDokaResponsible(id,params,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AssignDokaResponsibleMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useAssignDokaResponsibleHook>>>>

    export type AssignDokaResponsibleMutationError = ErrorType<ProblemDetails>
    export type AssignDokaResponsibleMutationVariables = {id: string;params: AssignDokaResponsibleParams}

    export const useAssignDokaResponsible = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useAssignDokaResponsibleHook>>>, TError,AssignDokaResponsibleMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useAssignDokaResponsibleHook>>>,
        TError,
        AssignDokaResponsibleMutationVariables,
        TContext
      > => {
      return useMutation(useAssignDokaResponsibleMutationOptions(options), queryClient);
    }
    /**
 * Assigns a new team to a request<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding-backoffice
 */
export const useChangeResponsibleTeamHook = () => {
        const changeResponsibleTeam = useCustomWelcomeClient<void>();

        return useCallback((
    id: string,
    params: ChangeResponsibleTeamParams,
 signal?: AbortSignal
) => {


        return changeResponsibleTeam(
          {url: `/requests/${id}/change-responsible-team`, method: 'PUT',
        params, signal
    },
          );
        }, [changeResponsibleTeam])
      }




export const useChangeResponsibleTeamMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useChangeResponsibleTeamHook>>>, TError,ChangeResponsibleTeamMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useChangeResponsibleTeamHook>>>, TError,ChangeResponsibleTeamMutationVariables, TContext> => {

const mutationKey = ['changeResponsibleTeam'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const changeResponsibleTeam =  useChangeResponsibleTeamHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useChangeResponsibleTeamHook>>>, ChangeResponsibleTeamMutationVariables> = (props) => {
          const {id,params} = props ?? {};

          return  changeResponsibleTeam(id,params,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ChangeResponsibleTeamMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useChangeResponsibleTeamHook>>>>

    export type ChangeResponsibleTeamMutationError = ErrorType<ProblemDetails>
    export type ChangeResponsibleTeamMutationVariables = {id: string;params: ChangeResponsibleTeamParams}

    export const useChangeResponsibleTeam = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useChangeResponsibleTeamHook>>>, TError,ChangeResponsibleTeamMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useChangeResponsibleTeamHook>>>,
        TError,
        ChangeResponsibleTeamMutationVariables,
        TContext
      > => {
      return useMutation(useChangeResponsibleTeamMutationOptions(options), queryClient);
    }
    /**
 * Returns a request in detail<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useGetRequestDetailHook = () => {
        const getRequestDetail = useCustomWelcomeClient<GetOnboardingRequestDetailResponse>();

        return useCallback((
    id: string,
 signal?: AbortSignal
) => {


        return getRequestDetail(
          {url: `/requests/${id}/detail`, method: 'GET', signal
    },
          );
        }, [getRequestDetail])
      }




export const getGetRequestDetailQueryKey = (id: string,) => {
    return [
    `/requests/${id}/detail`
    ] as const;
    }


export const useGetRequestDetailQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetRequestDetailHook>>>, TError = ErrorType<ProblemDetails>>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRequestDetailHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetRequestDetailQueryKey(id);

  const getRequestDetail =  useGetRequestDetailHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetRequestDetailHook>>>> = ({ signal }) => getRequestDetail(id, signal);





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRequestDetailHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetRequestDetailQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetRequestDetailHook>>>>
export type GetRequestDetailQueryError = ErrorType<ProblemDetails>


export function useGetRequestDetail<TData = Awaited<ReturnType<ReturnType<typeof useGetRequestDetailHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRequestDetailHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetRequestDetailHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetRequestDetailHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetRequestDetail<TData = Awaited<ReturnType<ReturnType<typeof useGetRequestDetailHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRequestDetailHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetRequestDetailHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetRequestDetailHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetRequestDetail<TData = Awaited<ReturnType<ReturnType<typeof useGetRequestDetailHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRequestDetailHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetRequestDetail<TData = Awaited<ReturnType<ReturnType<typeof useGetRequestDetailHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetRequestDetailHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetRequestDetailQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Allows a backoffice employee to hide a request.<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding-backoffice
 */
export const useHideRequestHook = () => {
        const hideRequest = useCustomWelcomeClient<void>();

        return useCallback((
    id: string,
 signal?: AbortSignal
) => {


        return hideRequest(
          {url: `/requests/${id}/hide`, method: 'PUT', signal
    },
          );
        }, [hideRequest])
      }




export const useHideRequestMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useHideRequestHook>>>, TError,HideRequestMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useHideRequestHook>>>, TError,HideRequestMutationVariables, TContext> => {

const mutationKey = ['hideRequest'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const hideRequest =  useHideRequestHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useHideRequestHook>>>, HideRequestMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  hideRequest(id,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type HideRequestMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useHideRequestHook>>>>

    export type HideRequestMutationError = ErrorType<ProblemDetails>
    export type HideRequestMutationVariables = {id: string}

    export const useHideRequest = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useHideRequestHook>>>, TError,HideRequestMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useHideRequestHook>>>,
        TError,
        HideRequestMutationVariables,
        TContext
      > => {
      return useMutation(useHideRequestMutationOptions(options), queryClient);
    }
    /**
 * Allows a backoffice employee to reset the additional data input to the data input state.<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding-backoffice
 */
export const useResetAdditionalDataInputHook = () => {
        const resetAdditionalDataInput = useCustomWelcomeClient<void>();

        return useCallback((
    id: string,
 signal?: AbortSignal
) => {


        return resetAdditionalDataInput(
          {url: `/requests/${id}/reset-additional-data-input`, method: 'POST', signal
    },
          );
        }, [resetAdditionalDataInput])
      }




export const useResetAdditionalDataInputMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useResetAdditionalDataInputHook>>>, TError,ResetAdditionalDataInputMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useResetAdditionalDataInputHook>>>, TError,ResetAdditionalDataInputMutationVariables, TContext> => {

const mutationKey = ['resetAdditionalDataInput'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const resetAdditionalDataInput =  useResetAdditionalDataInputHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useResetAdditionalDataInputHook>>>, ResetAdditionalDataInputMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  resetAdditionalDataInput(id,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ResetAdditionalDataInputMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useResetAdditionalDataInputHook>>>>

    export type ResetAdditionalDataInputMutationError = ErrorType<ProblemDetails>
    export type ResetAdditionalDataInputMutationVariables = {id: string}

    export const useResetAdditionalDataInput = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useResetAdditionalDataInputHook>>>, TError,ResetAdditionalDataInputMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useResetAdditionalDataInputHook>>>,
        TError,
        ResetAdditionalDataInputMutationVariables,
        TContext
      > => {
      return useMutation(useResetAdditionalDataInputMutationOptions(options), queryClient);
    }
    /**
 * Saves a draft of a request without validating<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useSaveRequestDraftHook = () => {
        const saveRequestDraft = useCustomWelcomeClient<void>();

        return useCallback((
    id: string,
    updateOnboardingRequestModel: UpdateOnboardingRequestModel,
 signal?: AbortSignal
) => {


        return saveRequestDraft(
          {url: `/requests/${id}/save`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: updateOnboardingRequestModel, signal
    },
          );
        }, [saveRequestDraft])
      }




export const useSaveRequestDraftMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useSaveRequestDraftHook>>>, TError,SaveRequestDraftMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useSaveRequestDraftHook>>>, TError,SaveRequestDraftMutationVariables, TContext> => {

const mutationKey = ['saveRequestDraft'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const saveRequestDraft =  useSaveRequestDraftHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useSaveRequestDraftHook>>>, SaveRequestDraftMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  saveRequestDraft(id,data,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SaveRequestDraftMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useSaveRequestDraftHook>>>>
    export type SaveRequestDraftMutationBody = UpdateOnboardingRequestModel
    export type SaveRequestDraftMutationError = ErrorType<ProblemDetails>
    export type SaveRequestDraftMutationVariables = {id: string;data: UpdateOnboardingRequestModel}

    export const useSaveRequestDraft = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useSaveRequestDraftHook>>>, TError,SaveRequestDraftMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useSaveRequestDraftHook>>>,
        TError,
        SaveRequestDraftMutationVariables,
        TContext
      > => {
      return useMutation(useSaveRequestDraftMutationOptions(options), queryClient);
    }
    /**
 * Updates the state of a request manually, so it's possible to skip states. Only for test purposes.
 */
export const useUpdateStateManuallyHook = () => {
        const updateStateManually = useCustomWelcomeClient<void>();

        return useCallback((
    id: string,
    params: UpdateStateManuallyParams,
 signal?: AbortSignal
) => {


        return updateStateManually(
          {url: `/requests/${id}/state`, method: 'POST',
        params, signal
    },
          );
        }, [updateStateManually])
      }




export const useUpdateStateManuallyMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useUpdateStateManuallyHook>>>, TError,UpdateStateManuallyMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useUpdateStateManuallyHook>>>, TError,UpdateStateManuallyMutationVariables, TContext> => {

const mutationKey = ['updateStateManually'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const updateStateManually =  useUpdateStateManuallyHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useUpdateStateManuallyHook>>>, UpdateStateManuallyMutationVariables> = (props) => {
          const {id,params} = props ?? {};

          return  updateStateManually(id,params,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateStateManuallyMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUpdateStateManuallyHook>>>>

    export type UpdateStateManuallyMutationError = ErrorType<ProblemDetails>
    export type UpdateStateManuallyMutationVariables = {id: string;params: UpdateStateManuallyParams}

    export const useUpdateStateManually = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useUpdateStateManuallyHook>>>, TError,UpdateStateManuallyMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useUpdateStateManuallyHook>>>,
        TError,
        UpdateStateManuallyMutationVariables,
        TContext
      > => {
      return useMutation(useUpdateStateManuallyMutationOptions(options), queryClient);
    }
    /**
 * Saves a request if the validation succeeds<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useSubmitRequestHook = () => {
        const submitRequest = useCustomWelcomeClient<void>();

        return useCallback((
    id: string,
    updateOnboardingRequestModel: UpdateOnboardingRequestModel,
 signal?: AbortSignal
) => {


        return submitRequest(
          {url: `/requests/${id}/submit`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: updateOnboardingRequestModel, signal
    },
          );
        }, [submitRequest])
      }




export const useSubmitRequestMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useSubmitRequestHook>>>, TError,SubmitRequestMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useSubmitRequestHook>>>, TError,SubmitRequestMutationVariables, TContext> => {

const mutationKey = ['submitRequest'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const submitRequest =  useSubmitRequestHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useSubmitRequestHook>>>, SubmitRequestMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  submitRequest(id,data,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SubmitRequestMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useSubmitRequestHook>>>>
    export type SubmitRequestMutationBody = UpdateOnboardingRequestModel
    export type SubmitRequestMutationError = ErrorType<ProblemDetails>
    export type SubmitRequestMutationVariables = {id: string;data: UpdateOnboardingRequestModel}

    export const useSubmitRequest = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useSubmitRequestHook>>>, TError,SubmitRequestMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useSubmitRequestHook>>>,
        TError,
        SubmitRequestMutationVariables,
        TContext
      > => {
      return useMutation(useSubmitRequestMutationOptions(options), queryClient);
    }
    /**
 * Validates a request without saving it<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useValidateRequestHook = () => {
        const validateRequest = useCustomWelcomeClient<void>();

        return useCallback((
    id: string,
    updateOnboardingRequestModel: UpdateOnboardingRequestModel,
 signal?: AbortSignal
) => {


        return validateRequest(
          {url: `/requests/${id}/validate`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: updateOnboardingRequestModel, signal
    },
          );
        }, [validateRequest])
      }




export const useValidateRequestMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useValidateRequestHook>>>, TError,ValidateRequestMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useValidateRequestHook>>>, TError,ValidateRequestMutationVariables, TContext> => {

const mutationKey = ['validateRequest'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const validateRequest =  useValidateRequestHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useValidateRequestHook>>>, ValidateRequestMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  validateRequest(id,data,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ValidateRequestMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useValidateRequestHook>>>>
    export type ValidateRequestMutationBody = UpdateOnboardingRequestModel
    export type ValidateRequestMutationError = ErrorType<ProblemDetails>
    export type ValidateRequestMutationVariables = {id: string;data: UpdateOnboardingRequestModel}

    export const useValidateRequest = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useValidateRequestHook>>>, TError,ValidateRequestMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useValidateRequestHook>>>,
        TError,
        ValidateRequestMutationVariables,
        TContext
      > => {
      return useMutation(useValidateRequestMutationOptions(options), queryClient);
    }
    /**
 * Sets the verification of a request to the given verify state<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useVerifyRequestHook = () => {
        const verifyRequest = useCustomWelcomeClient<void>();

        return useCallback((
    id: string,
    verifyOnboardingRequestModel: VerifyOnboardingRequestModel,
 signal?: AbortSignal
) => {


        return verifyRequest(
          {url: `/requests/${id}/verify`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: verifyOnboardingRequestModel, signal
    },
          );
        }, [verifyRequest])
      }




export const useVerifyRequestMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useVerifyRequestHook>>>, TError,VerifyRequestMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useVerifyRequestHook>>>, TError,VerifyRequestMutationVariables, TContext> => {

const mutationKey = ['verifyRequest'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const verifyRequest =  useVerifyRequestHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useVerifyRequestHook>>>, VerifyRequestMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  verifyRequest(id,data,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type VerifyRequestMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useVerifyRequestHook>>>>
    export type VerifyRequestMutationBody = VerifyOnboardingRequestModel
    export type VerifyRequestMutationError = ErrorType<ProblemDetails>
    export type VerifyRequestMutationVariables = {id: string;data: VerifyOnboardingRequestModel}

    export const useVerifyRequest = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useVerifyRequestHook>>>, TError,VerifyRequestMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useVerifyRequestHook>>>,
        TError,
        VerifyRequestMutationVariables,
        TContext
      > => {
      return useMutation(useVerifyRequestMutationOptions(options), queryClient);
    }
    /**
 * Creates a new request for existing customer<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useCreateRequestForExistingCustomerHook = () => {
        const createRequestForExistingCustomer = useCustomWelcomeClient<GetOnboardingRequestDetailResponse>();

        return useCallback((
    unknownNullCreateOnboardingRequestExistingModel?: unknown | null | CreateOnboardingRequestExistingModel,
 signal?: AbortSignal
) => {


        return createRequestForExistingCustomer(
          {url: `/requests/existing`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: unknownNullCreateOnboardingRequestExistingModel, signal
    },
          );
        }, [createRequestForExistingCustomer])
      }




export const useCreateRequestForExistingCustomerMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateRequestForExistingCustomerHook>>>, TError,CreateRequestForExistingCustomerMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateRequestForExistingCustomerHook>>>, TError,CreateRequestForExistingCustomerMutationVariables, TContext> => {

const mutationKey = ['createRequestForExistingCustomer'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const createRequestForExistingCustomer =  useCreateRequestForExistingCustomerHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useCreateRequestForExistingCustomerHook>>>, CreateRequestForExistingCustomerMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createRequestForExistingCustomer(data,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateRequestForExistingCustomerMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useCreateRequestForExistingCustomerHook>>>>
    export type CreateRequestForExistingCustomerMutationBody = unknown | null | CreateOnboardingRequestExistingModel | undefined
    export type CreateRequestForExistingCustomerMutationError = ErrorType<ProblemDetails>
    export type CreateRequestForExistingCustomerMutationVariables = {data?: unknown | null | CreateOnboardingRequestExistingModel}

    export const useCreateRequestForExistingCustomer = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateRequestForExistingCustomerHook>>>, TError,CreateRequestForExistingCustomerMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useCreateRequestForExistingCustomerHook>>>,
        TError,
        CreateRequestForExistingCustomerMutationVariables,
        TContext
      > => {
      return useMutation(useCreateRequestForExistingCustomerMutationOptions(options), queryClient);
    }
    /**
 * Creates a new request for existing customer in doka360<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding-dokathreesixty
 */
export const useCreateDoka360RequestForExistingCustomerHook = () => {
        const createDoka360RequestForExistingCustomer = useCustomWelcomeClient<GetOnboardingRequestDetailResponse>();

        return useCallback((
    unknownNullCreateOnboardingRequestExistingModel?: unknown | null | CreateOnboardingRequestExistingModel,
 signal?: AbortSignal
) => {


        return createDoka360RequestForExistingCustomer(
          {url: `/requests/existing/doka360`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: unknownNullCreateOnboardingRequestExistingModel, signal
    },
          );
        }, [createDoka360RequestForExistingCustomer])
      }




export const useCreateDoka360RequestForExistingCustomerMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateDoka360RequestForExistingCustomerHook>>>, TError,CreateDoka360RequestForExistingCustomerMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateDoka360RequestForExistingCustomerHook>>>, TError,CreateDoka360RequestForExistingCustomerMutationVariables, TContext> => {

const mutationKey = ['createDoka360RequestForExistingCustomer'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const createDoka360RequestForExistingCustomer =  useCreateDoka360RequestForExistingCustomerHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useCreateDoka360RequestForExistingCustomerHook>>>, CreateDoka360RequestForExistingCustomerMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createDoka360RequestForExistingCustomer(data,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateDoka360RequestForExistingCustomerMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useCreateDoka360RequestForExistingCustomerHook>>>>
    export type CreateDoka360RequestForExistingCustomerMutationBody = unknown | null | CreateOnboardingRequestExistingModel | undefined
    export type CreateDoka360RequestForExistingCustomerMutationError = ErrorType<ProblemDetails>
    export type CreateDoka360RequestForExistingCustomerMutationVariables = {data?: unknown | null | CreateOnboardingRequestExistingModel}

    export const useCreateDoka360RequestForExistingCustomer = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateDoka360RequestForExistingCustomerHook>>>, TError,CreateDoka360RequestForExistingCustomerMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useCreateDoka360RequestForExistingCustomerHook>>>,
        TError,
        CreateDoka360RequestForExistingCustomerMutationVariables,
        TContext
      > => {
      return useMutation(useCreateDoka360RequestForExistingCustomerMutationOptions(options), queryClient);
    }
    /**
 * Exports all onboarding requests created within an optional time range as a CSV or XLSX file. Only accessible by administrators.<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding-backoffice
 */
export const useExportRequestsHook = () => {
        const exportRequests = useCustomWelcomeClient<Blob>();

        return useCallback((
    params?: ExportRequestsParams,
 signal?: AbortSignal
) => {


        return exportRequests(
          {url: `/requests/export`, method: 'GET',
        params,
        responseType: 'blob', signal
    },
          );
        }, [exportRequests])
      }




export const getExportRequestsQueryKey = (params?: ExportRequestsParams,) => {
    return [
    `/requests/export`, ...(params ? [params] : [])
    ] as const;
    }


export const useExportRequestsQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useExportRequestsHook>>>, TError = ErrorType<ProblemDetails>>(params?: ExportRequestsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useExportRequestsHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getExportRequestsQueryKey(params);

  const exportRequests =  useExportRequestsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useExportRequestsHook>>>> = ({ signal }) => exportRequests(params, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useExportRequestsHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ExportRequestsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useExportRequestsHook>>>>
export type ExportRequestsQueryError = ErrorType<ProblemDetails>


export function useExportRequests<TData = Awaited<ReturnType<ReturnType<typeof useExportRequestsHook>>>, TError = ErrorType<ProblemDetails>>(
 params: undefined |  ExportRequestsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useExportRequestsHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useExportRequestsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useExportRequestsHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useExportRequests<TData = Awaited<ReturnType<ReturnType<typeof useExportRequestsHook>>>, TError = ErrorType<ProblemDetails>>(
 params?: ExportRequestsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useExportRequestsHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useExportRequestsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useExportRequestsHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useExportRequests<TData = Awaited<ReturnType<ReturnType<typeof useExportRequestsHook>>>, TError = ErrorType<ProblemDetails>>(
 params?: ExportRequestsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useExportRequestsHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useExportRequests<TData = Awaited<ReturnType<ReturnType<typeof useExportRequestsHook>>>, TError = ErrorType<ProblemDetails>>(
 params?: ExportRequestsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useExportRequestsHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useExportRequestsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Creates a new request for a new customer<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useCreateRequestForNewCustomerHook = () => {
        const createRequestForNewCustomer = useCustomWelcomeClient<GetOnboardingRequestDetailResponse>();

        return useCallback((
    createOnboardingRequestNewModel: CreateOnboardingRequestNewModel,
 signal?: AbortSignal
) => {


        return createRequestForNewCustomer(
          {url: `/requests/new`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createOnboardingRequestNewModel, signal
    },
          );
        }, [createRequestForNewCustomer])
      }




export const useCreateRequestForNewCustomerMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateRequestForNewCustomerHook>>>, TError,CreateRequestForNewCustomerMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateRequestForNewCustomerHook>>>, TError,CreateRequestForNewCustomerMutationVariables, TContext> => {

const mutationKey = ['createRequestForNewCustomer'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const createRequestForNewCustomer =  useCreateRequestForNewCustomerHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useCreateRequestForNewCustomerHook>>>, CreateRequestForNewCustomerMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createRequestForNewCustomer(data,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateRequestForNewCustomerMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useCreateRequestForNewCustomerHook>>>>
    export type CreateRequestForNewCustomerMutationBody = CreateOnboardingRequestNewModel
    export type CreateRequestForNewCustomerMutationError = ErrorType<ProblemDetails>
    export type CreateRequestForNewCustomerMutationVariables = {data: CreateOnboardingRequestNewModel}

    export const useCreateRequestForNewCustomer = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateRequestForNewCustomerHook>>>, TError,CreateRequestForNewCustomerMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useCreateRequestForNewCustomerHook>>>,
        TError,
        CreateRequestForNewCustomerMutationVariables,
        TContext
      > => {
      return useMutation(useCreateRequestForNewCustomerMutationOptions(options), queryClient);
    }
    /**
 * Creates a new request for a new customer in doka360<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding-dokathreesixty
 */
export const useCreateDoka360RequestForNewCustomerHook = () => {
        const createDoka360RequestForNewCustomer = useCustomWelcomeClient<GetOnboardingRequestDetailResponse>();

        return useCallback((
    createOnboardingRequestNewModel: CreateOnboardingRequestNewModel,
 signal?: AbortSignal
) => {


        return createDoka360RequestForNewCustomer(
          {url: `/requests/new/doka360`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createOnboardingRequestNewModel, signal
    },
          );
        }, [createDoka360RequestForNewCustomer])
      }




export const useCreateDoka360RequestForNewCustomerMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateDoka360RequestForNewCustomerHook>>>, TError,CreateDoka360RequestForNewCustomerMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateDoka360RequestForNewCustomerHook>>>, TError,CreateDoka360RequestForNewCustomerMutationVariables, TContext> => {

const mutationKey = ['createDoka360RequestForNewCustomer'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const createDoka360RequestForNewCustomer =  useCreateDoka360RequestForNewCustomerHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useCreateDoka360RequestForNewCustomerHook>>>, CreateDoka360RequestForNewCustomerMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createDoka360RequestForNewCustomer(data,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateDoka360RequestForNewCustomerMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useCreateDoka360RequestForNewCustomerHook>>>>
    export type CreateDoka360RequestForNewCustomerMutationBody = CreateOnboardingRequestNewModel
    export type CreateDoka360RequestForNewCustomerMutationError = ErrorType<ProblemDetails>
    export type CreateDoka360RequestForNewCustomerMutationVariables = {data: CreateOnboardingRequestNewModel}

    export const useCreateDoka360RequestForNewCustomer = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateDoka360RequestForNewCustomerHook>>>, TError,CreateDoka360RequestForNewCustomerMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useCreateDoka360RequestForNewCustomerHook>>>,
        TError,
        CreateDoka360RequestForNewCustomerMutationVariables,
        TContext
      > => {
      return useMutation(useCreateDoka360RequestForNewCustomerMutationOptions(options), queryClient);
    }
